import React from 'react';
import Button, { LoadingButton } from '@atlaskit/button';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import ModalDialog, {
	ModalTransition,
	ModalBody,
	ModalTitle,
	ModalHeader,
	ModalFooter,
} from '@atlaskit/modal-dialog';
import ShortcutScope from '@atlassian/jira-common-components-keyboard-shortcuts/src/shortcut-scope.tsx';
import { JSErrorBoundary } from '@atlassian/jira-error-boundaries/src/ui/js-error-boundary/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import {
	externalResourcesDeleteExperience,
	markDeleteExperienceFailed,
	ProductExperienceStart,
} from '../../../experiences';
import messages from './messages';
import type { Props } from './types';

export function DeleteDiscardPrompt(props: Props) {
	const { isOpen, isDeleting, onCancel, onProceed } = props;
	const { formatMessage } = useIntl();

	return (
		<ModalTransition>
			{isOpen && (
				<>
					<ProductExperienceStart ufoExperience={externalResourcesDeleteExperience} />
					<JSErrorBoundary
						id="externalResourcesDelete"
						packageName="jiraServicedeskExternalResourcesSettings"
						teamName="jsm-nebula"
						fallback="flag"
						onError={markDeleteExperienceFailed}
					>
						<ShortcutScope>
							<ModalDialog
								onClose={(_, analyticsEvent) => {
									fireUIAnalytics(analyticsEvent, 'externalResourceDeleteDiscardPrompt');
									onCancel();
									externalResourcesDeleteExperience.abort();
								}}
							>
								<ModalHeader>
									<ModalTitle appearance="warning">
										{formatMessage(messages.externalLinkDeleteDiscardPromptHeader)}
									</ModalTitle>
								</ModalHeader>

								<ModalBody>{formatMessage(messages.externalLinkDeleteDiscardPromptBody)}</ModalBody>
								<ModalFooter>
									<Button
										appearance="subtle-link"
										onClick={(_, analyticsEvent) => {
											fireUIAnalytics(analyticsEvent, 'externalResourceDeleteDiscardPromptCancel');
											onCancel();
											externalResourcesDeleteExperience.abort();
										}}
										isDisabled={isDeleting}
									>
										{formatMessage(messages.externalLinkDeleteDiscardPromptCancel)}
									</Button>
									<LoadingButton
										appearance="warning"
										onClick={(_, analyticsEvent) => {
											fireUIAnalytics(analyticsEvent, 'externalResourceDeleteDiscardPromptProceed');
											onProceed();
										}}
										isDisabled={isDeleting}
										isLoading={isDeleting}
									>
										{formatMessage(messages.externalLinkDeleteDiscardPromptProceed)}
									</LoadingButton>
								</ModalFooter>
							</ModalDialog>
						</ShortcutScope>
					</JSErrorBoundary>
				</>
			)}
		</ModalTransition>
	);
}

export default DeleteDiscardPrompt;
